import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Image, Button } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { logger } from '@marriott/shared/mi-helper-utils';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { getAEMTrackingProperties, isExternalLink, mangoDynamicMediaTransformation } from '../../utils/helpers';
import { MANGO_PARAMETERS } from '../../utils/constants';
import { ITrackingProperties, MangoPilotProps, Rendition } from './MangoPilot.types';
import { StyledMangoPilot } from './MangoPilot.styles';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export const MangoPilotComponent: FC<MangoPilotProps> = props => {
  const { datalayer } = useSession();
  const {
    mboxParameter,
    metaData,
    image,
    destinationUrl1,
    adData,
    openInNewTab = true,
    trackingProperties = {} as ITrackingProperties,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils?.isInEditor() ? false : true,
    isTargetContentError = false,
    displayEvergreenContent = true,
    styleclass,
  } = props;

  const { log } = logger({})('mangoPilot');

  const mangoTrackingEndpoint = MANGO_PARAMETERS.URL || '';

  const ref = useRef<HTMLAnchorElement>(null);
  const [isVisible, componentRef] = useIntersectionObserver();

  const [mangoAttributes, setMangoAttributes] = useState<{
    showMangoComponent: boolean;
    isMangoFromMbox: boolean;
    loading: boolean;
    mangoAssets: {
      altText: string;
      assetPath: string;
      renditions: Rendition[];
    } | null;
  }>({
    showMangoComponent: AuthoringUtils?.isInEditor() ? true : false,
    isMangoFromMbox: false,
    loading: true,
    mangoAssets: null,
  });

  useEffect(() => {
    if (isTargetContentLoaded && metaData?.isMango && !isTargetContentError) {
      const mangoAssets = mangoDynamicMediaTransformation(adData?.[0]?.assets);
      const desktopRendition = mangoAssets?.renditions?.[0];

      const isMangoFromMbox =
        mangoAssets && mangoAssets?.renditions?.length > 0 && desktopRendition?.renditionPath ? true : false;
      const showMangoComponent = isMangoFromMbox ? true : displayEvergreenContent && image?.assetPath ? true : false;

      handleMangoTracking(
        showMangoComponent,
        isMangoFromMbox,
        adData?.[0]?.custom_tracking_url,
        MANGO_PARAMETERS.LOADED
      );

      setMangoAttributes({
        ...mangoAttributes,
        showMangoComponent: showMangoComponent,
        isMangoFromMbox: isMangoFromMbox,
        loading: !isTargetContentLoaded,
        mangoAssets: mangoAssets,
      });
    } else if (isTargetContentLoaded) {
      setMangoAttributes({
        ...mangoAttributes,
        showMangoComponent: displayEvergreenContent && image?.assetPath ? true : false,
        isMangoFromMbox: false,
        loading: !isTargetContentLoaded,
        mangoAssets: null,
      });
    }
  }, [isTargetContentLoaded]);

  useEffect(() => {
    if (isVisible) {
      // This is for Analytics tracking
      handleMangoTracking(
        mangoAttributes.showMangoComponent,
        mangoAttributes.isMangoFromMbox,
        adData?.[0]?.impression_tracking_url
      );
    }
  }, [isVisible]);

  const trackingPropertiesDynamic = getAEMTrackingProperties(
    trackingProperties,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated'
  );

  const handleMangoTracking = (
    showMangoComponent: boolean,
    isMangoFromMbox: boolean,
    trackingURLFromUXL: string,
    customTracking?: string
  ) => {
    try {
      if (showMangoComponent && isMangoFromMbox && trackingURLFromUXL && trackingURLFromUXL !== '') {
        const mangoHeaders = new Headers();
        const urlObj = new URL(trackingURLFromUXL);
        const newParams = new URLSearchParams();
        const keyArray: { trackingData: string; clientName: string; [type: string]: string | undefined } = {
          trackingData: adData?.[0]?.tracking_data,
          clientName: MANGO_PARAMETERS.CLIENT_NAME,
          type: customTracking,
        };

        urlObj?.searchParams?.forEach((value: string, key: string) => {
          if (value === `{${key}}`) {
            if (key in keyArray && keyArray?.[key]) {
              newParams?.set(key, keyArray[key] as string);
            }
          } else if (key === MANGO_PARAMETERS.key && keyArray?.[key]) {
            newParams?.set(key, keyArray[key] as string);
          } else if (value !== `{${key}}`) {
            newParams?.set(key, value);
          }
        });

        const trackingURL = `${mangoTrackingEndpoint}?${newParams?.toString()}`;

        fetch(trackingURL, {
          method: 'GET',
          headers: mangoHeaders,
        });
      }
    } catch (error) {
      log.error('Error in mango pilot tracking api call:', error);
    }
  };

  return (
    <StyledMangoPilot
      data-testid="merch-MangoPilot"
      data-component-name="o-merch-MangoPilot"
      className={clsx(
        `justify-content-center mango-pilot ${
          isTargetContentError && String(displayEvergreenContent) === 'false' ? 'd-none' : 'd-flex'
        }`
      )}
      ref={componentRef}
    >
      {mangoAttributes.showMangoComponent || AuthoringUtils?.isInEditor() ? (
        <Button
          isLink={true}
          testId="button"
          callback={() =>
            handleMangoTracking(
              mangoAttributes.showMangoComponent,
              mangoAttributes.isMangoFromMbox,
              adData?.[0]?.click_tracking_url
            )
          }
          ref={ref}
          href={AuthoringUtils.isInEditor() ? destinationUrl1 : adData?.[0]?.assets?.ctaURL}
          target={
            isExternalLink(AuthoringUtils.isInEditor() ? destinationUrl1 : adData?.[0]?.assets?.ctaURL, openInNewTab)
              ? '_blank'
              : '_self'
          }
          className={`${styleclass} mango-pilot__width ${
            AuthoringUtils.isInEditor() ? 'mango-pilot__authoring_container' : ''
          }`}
          trackingProperties={{ ...trackingPropertiesDynamic, isMango: true, mboxName: mboxParameter }}
        >
          <div className={clsx('mango-pilot__image_container')}>
            {mangoAttributes.isMangoFromMbox && mangoAttributes.mangoAssets ? (
              <Image
                customClass={clsx('mango-pilot__image')}
                defaultImageURL={mangoAttributes?.mangoAssets?.assetPath}
                altText={mangoAttributes?.mangoAssets?.altText}
                renditions={mangoAttributes?.mangoAssets?.renditions}
              />
            ) : (
              <Image
                customClass={clsx('mango-pilot__image')}
                altText={image?.altText ?? ''}
                renditions={image?.renditions}
                dynamic={image?.dynamic}
                defaultImageURL={image?.assetPath}
              />
            )}
          </div>
        </Button>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {mangoAttributes.loading && (
            <div className="d-flex align-items-center mango-pilot__skelton">
              <a className={`mango-pilot__width ${styleclass}`}>
                <div className="mango-pilot__skelton-image skeleton-loader-composite"></div>
              </a>
            </div>
          )}
        </>
      )}
    </StyledMangoPilot>
  );
};

const MangoPilotConfig = {
  emptyLabel: 'MangoPilot',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/mangopilot`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingMangoPilot = (props: any) => {
  return (
    <EditableComponent
      config={MangoPilotConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MangoPilotComponent {...({} as any)} />}
    />
  );
};
